import { z } from 'zod';

export const FormTaskSchema = z.object({
    id: z.string().optional(),
    formSubmission: z.string().optional(),
    formTemplate: z.string().optional(),
    document: z.string().optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    assignedTo: z.string().optional(),
    assignedBy: z.string().optional(),
    dueDate: z.string().or(z.date()).optional(),
    status: z.enum(['waiting-assignment', 'assigned', 'in-progress', 'completed', 'cancelled']).default('waiting-assignment'),
    priority: z.enum(['low', 'medium', 'high', 'critical']).default('medium'),
    completionNotes: z.string().optional(),
    workflowInstanceId: z.string().optional(),
    workflowTaskId: z.string().optional(),
    workflowNodeId: z.string().optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
});

export const CreateFormTaskSchema = FormTaskSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});

export const UpdateFormTaskSchema = FormTaskSchema.partial();

/**
 * Persist / query SoT is `assignedTo`. `assignee` is an inbound alias for
 * older clients (e.g. FormTask assign UI).
 * @param {object} [input]
 * @returns {string|undefined}
 */
export function resolveFormTaskAssignedTo(input = {}) {
    const raw = input?.assignedTo ?? input?.assignee;
    if (raw == null || raw === '') return undefined;
    if (typeof raw === 'object') {
        const id = raw._id ?? raw.id;
        if (id == null || id === '') return undefined;
        return String(id);
    }
    return String(raw);
}

/**
 * Normalize an assign body onto `assignedTo` and drop the inbound alias.
 * @param {object} [data]
 */
export function toFormTaskAssignPayload(data = {}) {
    const { assignee, ...rest } = data;
    const assignedTo = resolveFormTaskAssignedTo(data);
    if (!assignedTo) return { ...rest };
    return { ...rest, assignedTo };
}

export const AssignFormTaskSchema = z.object({
    assignedTo: z.string().min(1).optional(),
    assignee: z.string().min(1).optional(),
    dueDate: z.string().or(z.date()).optional(),
    description: z.string().optional(),
}).superRefine((data, ctx) => {
    if (!resolveFormTaskAssignedTo(data)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'assignedTo is required',
            path: ['assignedTo'],
        });
    }
}).transform((data) => toFormTaskAssignPayload(data));
